import { useState } from "react";
import { X } from "lucide-react";
import SafeHtml from "./SafeHtml";

/**
 * PolicyDialog — a reusable trigger + modal for surfacing HTML policy content.
 * Used for House rules (per-property), Privacy policy, Terms, Stay policy, etc.
 *
 * Usage:
 *   <PolicyDialog trigger={<button>House rules</button>} title="House rules" html={property.house_rules_html} />
 *   <PolicyDialog triggerLabel="Privacy policy" title="Privacy policy" html={cfg.privacy_html} />
 */
export default function PolicyDialog({
  trigger,
  triggerLabel,
  triggerClassName,
  title,
  subtitle,
  html,
  emptyFallback,
  testId = "policy",
}) {
  const [open, setOpen] = useState(false);

  const defaultTrigger = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      data-testid={`${testId}-trigger`}
      className={triggerClassName || "underline underline-offset-2 text-forest hover:text-gold"}
    >
      {triggerLabel}
    </button>
  );

  return (
    <>
      {trigger
        ? <span onClick={() => setOpen(true)} className="inline-flex">{trigger}</span>
        : defaultTrigger}

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${testId}-title`}
          data-testid={`${testId}-dialog`}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-noir/60 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-parchment p-6 shadow-soft sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              data-testid={`${testId}-close`}
              aria-label="Close"
              className="absolute right-4 top-4 rounded-full border border-forest/20 bg-white/70 p-2 text-forest hover:bg-white"
            >
              <X size={16} />
            </button>
            <p className="text-[10px] uppercase tracking-[0.22em] text-amber">{title}</p>
            {subtitle && <h2 id={`${testId}-title`} className="mt-2 font-display text-2xl text-noir sm:text-3xl">{subtitle}</h2>}
            <div className="editorial-body mt-5 space-y-4 text-sm">
              {html
                ? <SafeHtml html={html} className="prose prose-sm max-w-none text-noir/85 [&_p]:mb-3" testId={`${testId}-content`} />
                : (emptyFallback || <p className="text-noir/60">We&apos;ll share the full details when your booking is confirmed — message us anytime for specifics.</p>)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
